import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { ArrowUpRight, ChevronDown, Menu, X } from "lucide-react";

import { Logo, Wordmark } from "./Logo";
import { SmartLink } from "./SmartLink";
import { PILLARS } from "../data/capabilities";

/**
 * Four things in the bar, and that is the whole design constraint.
 *
 * The brief asked for six top-level links; six across a bar is how a site
 * starts looking like an agency. Consulting, Software Development and
 * Products are the three answers to one question, so they live inside
 * "What we do" as a menu — which also lets each carry its one-line
 * explanation, something a flat link never could.
 */

const FLAT_LINKS = [
  { href: "/work", label: "Work" },
  { href: "/about", label: "About" },
];

export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Any navigation closes both menus. Without this the mobile drawer stays
  // open over the page you just navigated to.
  useEffect(() => {
    setOpen(false);
    setMenu(false);
  }, [pathname]);

  useEffect(() => {
    if (!menu) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenu(false);
    };
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenu(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [menu]);

  // Hover-open with a close delay, so crossing the gap between the trigger
  // and the panel does not snap it shut.
  const hoverOpen = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setMenu(true);
  };
  const hoverClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setMenu(false), 120);
  };

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);
  const pillarActive = PILLARS.some((p) => isActive(p.href)) || isActive("/what-we-do");

  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`sticky top-0 z-50 transition-colors ${
        scrolled || open
          ? "border-b border-white/5 bg-ink-950/80 backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-50 focus:rounded-full focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-ink-950"
      >
        Skip to content
      </a>

      <div className="container flex h-16 items-center justify-between gap-3">
        <SmartLink
          href="/"
          className="flex items-center gap-2 text-base font-semibold tracking-tight"
          aria-label="astalabista — home"
        >
          <Logo className="h-7 w-7" id="asta-mark-nav" />
          <Wordmark />
        </SmartLink>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
          <div
            ref={menuRef}
            className="relative"
            onMouseEnter={hoverOpen}
            onMouseLeave={hoverClose}
          >
            <button
              type="button"
              aria-expanded={menu}
              aria-haspopup="true"
              onClick={() => setMenu((v) => !v)}
              className={`inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm transition-colors ${
                pillarActive ? "text-white" : "text-white/70 hover:text-white"
              }`}
            >
              What we do
              <ChevronDown
                className={`size-3.5 transition-transform ${menu ? "rotate-180" : ""}`}
              />
            </button>

            <AnimatePresence>
              {menu ? (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 4 }}
                  transition={{ duration: 0.16 }}
                  className="absolute left-1/2 top-full w-[440px] -translate-x-1/2 pt-2"
                >
                  <div className="glass overflow-hidden p-2 shadow-2xl">
                    {PILLARS.map((p) => (
                      <SmartLink
                        key={p.key}
                        href={p.href}
                        className="block rounded-xl px-3 py-2.5 transition-colors hover:bg-white/[0.06]"
                      >
                        <div className="flex items-center gap-2 text-sm font-semibold text-white">
                          {p.title}
                          <ArrowUpRight className="size-3.5 text-white/35" />
                        </div>
                        <div className="mt-0.5 text-xs leading-relaxed text-white/55">
                          {p.lede}
                        </div>
                      </SmartLink>
                    ))}
                    <SmartLink
                      href="/what-we-do"
                      className="mt-1 block rounded-xl border-t border-white/5 px-3 py-2.5 text-xs font-medium text-white/60 transition-colors hover:bg-white/[0.06] hover:text-white"
                    >
                      See how the three fit together →
                    </SmartLink>
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>

          {FLAT_LINKS.map((l) => (
            <SmartLink
              key={l.href}
              href={l.href}
              className={`rounded-full px-3 py-2 text-sm transition-colors ${
                isActive(l.href) ? "text-white" : "text-white/70 hover:text-white"
              }`}
            >
              {l.label}
            </SmartLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <SmartLink
            href="/contact"
            className="group hidden items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-semibold text-ink-950 shadow-glow transition-transform hover:-translate-y-0.5 sm:inline-flex"
          >
            Start a project
            <ArrowUpRight className="size-4 transition-transform group-hover:rotate-45" />
          </SmartLink>
          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="grid size-10 place-items-center rounded-md text-white/85 transition-colors hover:bg-white/5 md:hidden"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer — flat, because a nested menu on a phone is a trap. */}
      <AnimatePresence>
        {open ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden border-t border-white/5 bg-ink-950/95 backdrop-blur md:hidden"
          >
            <div className="container flex flex-col gap-0.5 py-3">
              {[
                { href: "/what-we-do", label: "What we do" },
                ...PILLARS.map((p) => ({ href: p.href, label: p.title, nested: true })),
                ...FLAT_LINKS,
              ].map((l) => (
                <SmartLink
                  key={l.href + l.label}
                  href={l.href}
                  className={`rounded-md px-3 py-3 text-base font-medium transition-colors hover:bg-white/5 ${
                    "nested" in l && l.nested ? "pl-7 text-sm text-white/65" : "text-white/90"
                  }`}
                >
                  {l.label}
                </SmartLink>
              ))}
              <SmartLink
                href="/contact"
                className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-full bg-white px-4 py-3 text-base font-semibold text-ink-950 shadow-glow"
              >
                Start a project
                <ArrowUpRight className="size-4" />
              </SmartLink>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.header>
  );
}
